import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./actaTemplate.css";
import logoHabitia from "../../assets/logo.png";
// Sobre el azul va el logo blanco: html2canvas (el PDF) ignora `filter`, y el
// logo azul con filtro salía invisible en la portada y el cierre.
import logoBlanco from "../../assets/habitia-logo-white.png";
import {
  calcularQuorum,
  calcularVotacion,
  unidadesDePersona,
} from "../../utils/actaVoting";
import {
  formatFechaSlash,
  formatFechaLarga,
  pad2,
  nombreVotante,
} from "../../utils/actaConfig";
import { umbralQuorum as umbralPorConvocatoria } from "./actaFlujo";
import { paginar, partirParrafo, tramosConFormato, bloquesDeTexto, ALTO_UTIL_PX, MARGEN_SEGURIDAD_PX } from "./actaPaginacion";

// Documento ACTA DE ASAMBLEA: recreación del diseño hifi en React.
// Es un componente de presentación puro: recibe el acta + el padrón (edificio,
// copropietarios) y deriva quórum y resultados de votación con utils/actaVoting.
// En un acta publicada el Editor le pasa el padrón congelado (padronSnapshot):
// lo que se ve es lo mismo que se votó y se imprimió.
// El umbral de quórum es el mismo que usan el formulario y el snapshot de
// resultados (umbralQuorum en actaFlujo.js).
//
// Hojas: portada, el contenido (apertura, asistencia, orden del día y
// resoluciones) que fluye en bloques medidos y se reparte en A4 con
// actaPaginacion.js, y el cierre con las firmas. Los bloques se miden en un
// contenedor oculto al ancho de la hoja (sin el zoom de la vista previa).
//
// Props: acta, edificio, copropietarios, zoom (solo la vista previa; el PDF se
// captura siempre sin zoom).

const fmtMetodo = (n, metodo) =>
  metodo === "coeficiente" ? `${Number(n).toLocaleString("es-UY", { maximumFractionDigits: 1 })} %` : String(n);

/** "Folio N° 12 · Libro I", lo que haya; "" si no hay ninguno. */
const folioLibro = (acta) =>
  [acta.folio ? `Folio N° ${acta.folio}` : null, acta.libro ? `Libro ${acta.libro}` : null].filter(Boolean).join(" · ");

/** "20:30 — 21:45 hs", o lo que se sepa; "—" si no hay ninguna de las dos. */
function horario(acta) {
  const a = acta.horaApertura;
  const c = acta.horaCierre;
  if (a && c) return `${a} — ${c} hs`;
  if (a) return `${a} hs`;
  if (c) return `Cierre ${c} hs`;
  return "—";
}

/** "1 h 15 min" entre apertura y cierre (HH:MM); null si falta alguna. */
function duracion(apertura, cierre) {
  const m = (h) => {
    const r = /^(\d{1,2}):(\d{2})$/.exec(String(h || ""));
    return r ? Number(r[1]) * 60 + Number(r[2]) : null;
  };
  const a = m(apertura);
  const c = m(cierre);
  if (a == null || c == null) return null;
  const total = (c - a + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const min = total % 60;
  return h ? `${h} h${min ? ` ${min} min` : ""}` : `${min} min`;
}

/** Párrafos de un texto libre, con los muy largos partidos en tramos. */
const parrafosDe = (texto, max) =>
  String(texto || "")
    .split(/\n+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .flatMap((x) => partirParrafo(x, max));

// Tope de una caja de resolución por bloque (entra holgada en una hoja).
const MAX_CARACTERES_CAJA = 1400;

/**
 * Renglones con formato ([{ vineta, sigue, texto }] de actaPaginacion): cada
 * renglón suelto es un párrafo y las viñetas seguidas forman una lista. La
 * marca va como texto: html2canvas (el PDF) no siempre dibuja list-style.
 */
function Renglones({ lineas, claseParrafo }) {
  const grupos = [];
  lineas.forEach((l) => {
    const enLista = l.vineta || l.sigue;
    const ultimo = grupos[grupos.length - 1];
    if (enLista && ultimo && ultimo.lista) ultimo.items.push(l);
    else grupos.push({ lista: enLista, items: [l] });
  });
  return grupos.map((g, i) =>
    g.lista ? (
      <ul key={i} className="vinetas">
        {g.items.map((l, k) => (
          <li key={k}>
            <span className="marca" aria-hidden="true">{l.sigue ? "" : "•"}</span>
            <span>{l.texto}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p key={i} className={claseParrafo}>{g.items[0].texto}</p>
    )
  );
}

const PageFooter = ({ nombre, n, total }) => (
  <div className="pfooter">
    <div className="br">
      <img src={logoHabitia} alt="" />
      habitia · Acta de Asamblea{nombre ? ` · ${nombre}` : ""}
    </div>
    <div className="pn">{pad2(n)} / {pad2(total)}</div>
  </div>
);

// ───────────────────────── Portada ─────────────────────────
function CoverPage({ acta }) {
  const tipoLabel = acta.tipo === "extraordinaria" ? "Extraordinaria" : "Ordinaria";
  const convLabel = acta.convocatoria === "primera" ? "Primera" : "Segunda";
  const fl = folioLibro(acta);
  return (
    <section className="page cover">
      <div className="cover-top">
        <img src={logoBlanco} alt="habitia" />
        <div className="meta">
          <b>Acta de Asamblea</b>
          {acta.actaNumero ? <span>N° {acta.actaNumero}</span> : null}
        </div>
      </div>
      <div className="h-stage" aria-hidden="true">
        <div className="h-mark">
          <div className="bar-l" />
          <div className="bar-c" />
          <div className="bar-r" />
        </div>
      </div>
      <div className="cover-copy">
        <span className="eyb">{acta.epigrafe || "Asamblea de Copropietarios"}</span>
        <h1>
          Acta de<br />
          Asamblea<br />
          <em>{tipoLabel}.</em>
        </h1>
        <p className="lead">{acta.leadPortada}</p>
      </div>
      <div className={`cover-bottom${fl ? "" : " tres"}`}>
        <div><div className="lbl">Edificio</div><div className="val">{acta.edificioNombre || "—"}</div></div>
        <div><div className="lbl">Fecha</div><div className="val">{formatFechaSlash(acta.fecha)}</div></div>
        <div>
          <div className="lbl">Convocatoria</div>
          <div className="val">{convLabel}{acta.horaApertura ? ` · ${acta.horaApertura} hs` : ""}</div>
        </div>
        {fl && (
          <div>
            <div className="lbl">Folio · Libro</div>
            <div className="val">{fl}</div>
          </div>
        )}
      </div>
    </section>
  );
}

// ───────────────────── Bloques del contenido ─────────────────────

function AsistCabeza({ titulo }) {
  return (
    <>
      <h4 className="sub-h">{titulo}</h4>
      <div className="asist-cabeza">
        <div className="asist-row head">
          <div>Unidad</div>
          <div>Copropietario</div>
          <div>Representación / Poder</div>
        </div>
      </div>
    </>
  );
}

/**
 * La secuencia de bloques del contenido. Cada bloque: { key, render(ctx),
 * juntoAlSiguiente?, cont?, espacio?, tabla? }. `espacio` es el aire de arriba
 * (no va si el bloque abre la hoja); `tabla` marca las filas de una tabla que
 * se cierra con bordes redondeados al final de cada tramo. `conts` tiene la
 * cabecera de continuación de cada grupo.
 */
function construirBloques({ acta, edificio, coproById, quorum }) {
  const bloques = [];
  const conts = {};
  const add = (b) => bloques.push(b);

  // ── 00 · Apertura ──
  const tipoAsamblea = acta.tipo === "extraordinaria" ? "Asamblea Extraordinaria" : "Asamblea Ordinaria";
  const conv = acta.convocatoria === "primera" ? "primera" : "segunda";
  const aperturaTexto = acta.textoApertura?.trim()
    ? acta.textoApertura
    : `En la ciudad de ${acta.ciudad || "—"}, a los ${formatFechaLarga(acta.fecha)}, siendo las ${acta.horaApertura || "—"} horas y en ${conv} convocatoria, se reúnen en ${tipoAsamblea} los copropietarios del Edificio ${acta.edificioNombre || "—"}${acta.padron ? `, padrón N° ${acta.padron}` : ""}.`;
  const quorumPct = `${Number(quorum.coeficientePresente).toLocaleString("es-UY", { maximumFractionDigits: 1 })} %`;
  const dur = duracion(acta.horaApertura, acta.horaCierre);

  add({
    key: "ap-cabeza",
    juntoAlSiguiente: true,
    render: () => (
      <>
        <span className="sec-tag">00 · Apertura</span>
        <h2 className="big">Convocatoria, asistencia y <em>orden del día.</em></h2>
      </>
    ),
  });
  // Un texto de apertura largo se parte (ningún bloque puede ser más alto que una hoja).
  parrafosDe(aperturaTexto).forEach((par, k) => {
    add({ key: `ap-lead-${k}`, render: () => <p className="lead lead-parrafo">{par}</p> });
  });
  add({
    key: "ap-ribbon",
    render: () => (
      <div className="ribbon">
        <div className="rib-card">
          <div className="lbl">Apertura</div>
          <div className="val">{acta.horaApertura ? `${acta.horaApertura} hs` : "—"}</div>
          <div className="note">{conv === "primera" ? "Primera" : "Segunda"} convocatoria</div>
        </div>
        <div className="rib-card">
          <div className="lbl">Unidades presentes</div>
          <div className="val">{quorum.unidadesPresentes} / {quorum.unidadesTotales}</div>
          <div className="note">Incl. poderes</div>
        </div>
        <div className="rib-card">
          <div className="lbl">Quórum</div>
          <div className="val">{quorumPct}</div>
          <div className={`note ${quorum.habilitante ? "ok" : "no"}`}>
            {quorum.habilitante ? "Habilitante" : "No habilitante"}
          </div>
        </div>
        <div className="rib-card">
          <div className="lbl">Cierre</div>
          <div className="val">{acta.horaCierre ? `${acta.horaCierre} hs` : "—"}</div>
          <div className="note">{dur ? `${dur} de sesión` : " "}</div>
        </div>
      </div>
    ),
  });

  // Asistencia: una fila por bloque, para poder partir la tabla entre hojas.
  const unidades = edificio.unidades || [];
  if (unidades.length > 0) {
    add({ key: "asist-cabeza", juntoAlSiguiente: true, render: () => <AsistCabeza titulo="Asistencia" /> });
    conts.asist = () => <AsistCabeza titulo="Asistencia (continuación)" />;
    for (const u of unidades) {
      const fila = (acta.asistencia || []).find((a) => String(a.unidadId) === String(u.id)) || {};
      const presente = !!fila.presente;
      const titular = coproById.get(String(u.titularId));
      const votanteId = fila.votanteId || u.titularId;
      const votanteNom = nombreVotante(votanteId, acta.asistencia, coproById);
      // Poderes: otras unidades que esta misma persona carga en la sesión.
      const poderes = presente
        ? unidadesDePersona(votanteId, acta)
            .filter((uid) => String(uid) !== String(u.id))
            .map((uid) => unidades.find((x) => String(x.id) === String(uid))?.etiqueta || uid)
        : [];
      let repr;
      if (!presente) repr = "Ausente";
      else if (String(votanteId) === String(u.titularId)) repr = "Presente";
      else repr = <>Presente · representada por <b>{votanteNom}</b></>;
      add({
        key: `asist-${u.id}`,
        cont: "asist",
        tabla: "asist",
        render: ({ ultima }) => (
          <div className={`asist-fila${ultima ? " ultima" : ""}`}>
            <div className={`asist-row ${presente ? "" : "ausente"}`}>
              <div className="u">{u.etiqueta}</div>
              <div className="name">{titular?.nombre || "—"}</div>
              <div className="pwr">
                {repr}
                {/* Las etiquetas ya dicen qué es ("Apto 102", "Local 2"): mismas que el padrón congelado. */}
                {poderes.length > 0 && <> · <b>Poder: {poderes.join(", ")}</b></>}
              </div>
            </div>
          </div>
        ),
      });
    }
    add({
      key: "asist-adm",
      cont: "asist",
      tabla: "asist",
      render: ({ ultima }) => (
        <div className={`asist-fila${ultima ? " ultima" : ""}`}>
          <div className="asist-row">
            <div className="u" />
            <div className="name">{acta.administracion || "Habitia"}</div>
            <div className="pwr">{acta.administracionDetalle ? `Administración · ${acta.administracionDetalle}` : "Administración"}</div>
          </div>
        </div>
      ),
    });
  }

  // Orden del día: dos columnas, una fila de la grilla por bloque.
  const puntos = acta.ordenDelDia || [];
  if (puntos.length > 0) {
    add({ key: "agenda-cabeza", juntoAlSiguiente: true, render: () => <h4 className="sub-h">Orden del día</h4> });
    conts.agenda = () => <h4 className="sub-h">Orden del día (continuación)</h4>;
    let par = [];
    let nFila = 0;
    const cerrarPar = () => {
      if (!par.length) return;
      const items = par;
      par = [];
      nFila += 1;
      add({
        key: `agenda-${nFila}`,
        cont: "agenda",
        render: () => (
          <div className="agenda-fila">
            {items.map((it) => (
              <div className="agenda-row" key={it.n}>
                <span className="n">{pad2(it.n)}</span>
                <span className="t">{it.titulo || <em style={{ color: "#9AA0AB" }}>Sin título</em>}</span>
              </div>
            ))}
          </div>
        ),
      });
    };
    puntos.forEach((p, i) => {
      const prevSec = i > 0 ? puntos[i - 1].seccion : null;
      if (p.seccion && p.seccion !== prevSec) {
        cerrarPar();
        add({
          key: `agenda-sec-${i}`,
          cont: "agenda",
          juntoAlSiguiente: true,
          render: () => <div className="agenda-fila sec"><div className="agenda-sec">{p.seccion}</div></div>,
        });
      }
      par.push({ n: i + 1, titulo: p.titulo });
      if (par.length === 2) cerrarPar();
    });
    cerrarPar();

    // ── 01 · Resoluciones ──
    add({
      key: "res-cabeza",
      juntoAlSiguiente: true,
      espacio: 36,
      render: () => (
        <>
          <span className="sec-tag">01 · Resoluciones</span>
          <h2 className="big">Resoluciones<em>.</em></h2>
        </>
      ),
    });
    puntos.forEach((p, i) => {
      const numero = i + 1;
      // Claves por posición: un id repetido o faltante (actas viejas) no pisa otro punto.
      const id = `p${i}`;
      const grupo = `p:${i}`;
      const prevSec = i > 0 ? puntos[i - 1].seccion : null;
      const conSeccion = p.seccion && p.seccion !== prevSec;
      if (conSeccion) {
        add({ key: `res-sec-${i}`, juntoAlSiguiente: true, espacio: 12, render: () => <h4 className="sub-h">{p.seccion}</h4> });
      }
      const esInformativa = p.tipoResolucion === "informativa";
      const res = esInformativa ? null : calcularVotacion(p.votacion, acta, edificio, p.tipoResolucion);
      const tipoLabel = p.tipoResolucion === "unanime" ? "Unánime" : "Mayoría";
      // Saltos de renglón y viñetas: los del formulario se respetan en el acta.
      const parrafos = bloquesDeTexto(p.cuerpo);
      const tramosCaja = tramosConFormato(p.textoResolucion, MAX_CARACTERES_CAJA);
      const conCaja = tramosCaja.length > 0;
      const hayCuerpo = parrafos.length > 0 || conCaja || !!res;

      add({
        key: id,
        juntoAlSiguiente: hayCuerpo,
        espacio: i === 0 || conSeccion ? 12 : 24,
        render: () => (
          <div className="art-head">
            <div className="art-num">Punto {pad2(numero)}</div>
            <h3 className="art-title">{p.titulo || "—"}</h3>
          </div>
        ),
      });
      conts[grupo] = () => (
        <div className="art-cont">
          <span>Punto {pad2(numero)}</span>
          <span className="t">{p.titulo || "—"} (continuación)</span>
        </div>
      );
      parrafos.forEach((par, k) => {
        // El último párrafo va con la caja de la resolución: la caja no abre una hoja sola.
        const ultimo = k === parrafos.length - 1;
        add({
          key: `${id}-${k}`,
          cont: grupo,
          juntoAlSiguiente: ultimo && conCaja,
          render: () => <div className="art-parrafo"><Renglones lineas={par} /></div>,
        });
      });
      tramosCaja.forEach((tramo, k) => {
        const sigue = k > 0 ? " (continuación)" : "";
        const ultimoTramo = k === tramosCaja.length - 1;
        if (!esInformativa) {
          add({
            key: `${id}-reso-${k}`,
            cont: grupo,
            juntoAlSiguiente: ultimoTramo && !!res,
            render: () => (
              <div className={`reso ${p.tipoResolucion === "unanime" ? "unanime" : ""}`}>
                <div className="reso-tag">Resolución · {tipoLabel}{sigue}</div>
                <div className="reso-body"><Renglones lineas={tramo} /></div>
              </div>
            ),
          });
          return;
        }
        add({
          key: `${id}-info-${k}`,
          cont: grupo,
          render: () => (
            <div className="callout">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="11" x2="12" y2="16" strokeLinecap="round" />
                  <circle cx="12" cy="8" r="1" fill="currentColor" />
                </svg>
              </div>
              <div>
                <p className="ttl">Informativo{sigue}</p>
                <div className="tx"><Renglones lineas={tramo} /></div>
              </div>
            </div>
          ),
        });
      });
      if (res) {
        add({
          key: `${id}-voto`,
          cont: grupo,
          render: () => (
            <div className="vote">
              <div className="seg fav">A favor <b>{fmtMetodo(res.favor, res.metodo)}</b></div>
              <div className="seg con">En contra <b>{fmtMetodo(res.contra, res.metodo)}</b></div>
              <div className="seg abs">Abstención <b>{fmtMetodo(res.abstencion, res.metodo)}</b></div>
              <div className={`seg res ${res.aprobado ? "ok" : "no"}`}>{res.aprobado ? "Aprobada" : "No aprobada"}</div>
            </div>
          ),
        });
      }
    });
  }
  return { bloques, conts };
}

function Bloque({ b, primero, ultima }) {
  const estilo = b.espacio && !primero ? { paddingTop: b.espacio } : undefined;
  return (
    <div className="bloque" style={estilo}>
      {b.render({ ultima })}
    </div>
  );
}

// ───────────────────── Última página — Cierre + firmas ─────────────────────
// Fondo blanco: se imprime y se firma a mano.
function ClosingPage({ acta, coproById, n, total }) {
  const tipoLabel = acta.tipo === "extraordinaria" ? "Extraordinaria" : "Ordinaria";
  const convLabel = acta.convocatoria === "primera" ? "1ª" : "2ª";
  const pres = coproById.get(String(acta.presidenciaId));
  const sec = coproById.get(String(acta.secretariaId));
  const cierre = acta.textoCierre || "";
  const leadCierre = acta.horaCierre
    ? `Siendo las ${acta.horaCierre} horas, y ${cierre.charAt(0).toLowerCase()}${cierre.slice(1)}`
    : cierre;
  // Sin nombre, la línea queda en blanco para firmar.
  const vacio = " ";
  return (
    <section className="page closing">
      <div className="top-iso"><img src={logoHabitia} alt="habitia" /></div>
      <h2>
        Sin más asuntos<br />que tratar,<br /><em>se levanta sesión.</em>
      </h2>
      <p className="lead">{leadCierre}</p>

      <div className="grid">
        <div>
          <div className="lbl">Edificio</div>
          <div className="val">{acta.edificioNombre || "—"}{acta.padron ? ` · Padrón ${acta.padron}` : ""}</div>
        </div>
        <div>
          <div className="lbl">Tipo · Convocatoria</div>
          <div className="val">{tipoLabel} · {convLabel}</div>
        </div>
        <div>
          <div className="lbl">Apertura — Cierre</div>
          <div className="val">{horario(acta)}</div>
        </div>
        <div>
          <div className="lbl">Folio · Libro</div>
          <div className="val">{folioLibro(acta) || "—"}</div>
        </div>
      </div>

      <div className="sigs-w">
        <div className="sig-w">
          <div className="linea" />
          <div className="role">Presidencia</div>
          <div className="name">{acta.presidenciaNombre || pres?.nombre || vacio}</div>
          <div className="sub">{acta.presidenciaSub || (pres?.ci ? `C.I. ${pres.ci}` : "")}</div>
        </div>
        <div className="sig-w">
          <div className="linea" />
          <div className="role">Secretaría</div>
          <div className="name">{acta.secretariaNombre || sec?.nombre || vacio}</div>
          <div className="sub">{acta.secretariaSub || (sec?.ci ? `C.I. ${sec.ci}` : "")}</div>
        </div>
        <div className="sig-w">
          <div className="linea" />
          <div className="role">Administración</div>
          <div className="name">{acta.administracion || "Habitia"}</div>
          <div className="sub">{acta.administracionDetalle || ""}</div>
        </div>
      </div>

      <div className="pfooter-w">
        <div>habitia · Acta de Asamblea{acta.edificioNombre ? ` · ${acta.edificioNombre}` : ""}</div>
        <div>{pad2(n)} / {pad2(total)}</div>
      </div>
    </section>
  );
}

const mismasMedidas = (a, b) => {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Math.abs((a[k] || 0) - (b[k] || 0)) < 0.5);
};

const ActaTemplate = forwardRef(function ActaTemplate({ acta, edificio, copropietarios, zoom = 1 }, ref) {
  const coproById = useMemo(() => {
    const m = new Map();
    for (const c of copropietarios || []) m.set(String(c.id), c);
    return m;
  }, [copropietarios]);

  const quorum = useMemo(
    () => calcularQuorum(acta, edificio, umbralPorConvocatoria(acta.convocatoria)),
    [acta, edificio]
  );

  const { bloques, conts } = useMemo(
    () => construirBloques({ acta, edificio, coproById, quorum }),
    [acta, edificio, coproById, quorum]
  );

  // Alto de cada bloque (y de cada cabecera de continuación) al ancho de la hoja.
  const medidorRef = useRef(null);
  const [medidas, setMedidas] = useState({});
  const [fuentes, setFuentes] = useState(0);
  useLayoutEffect(() => {
    const raiz = medidorRef.current;
    if (!raiz) return;
    const nuevas = {};
    raiz.querySelectorAll("[data-medida]").forEach((el) => {
      nuevas[el.getAttribute("data-medida")] = el.getBoundingClientRect().height;
    });
    setMedidas((prev) => (mismasMedidas(prev, nuevas) ? prev : nuevas));
  }, [bloques, conts, fuentes]);
  // La tipografía puede llegar después del primer render: se vuelve a medir.
  useEffect(() => {
    const f = typeof document !== "undefined" ? document.fonts : null;
    if (!f) return undefined;
    let vivo = true;
    const otra = () => vivo && setFuentes((n) => n + 1);
    if (f.ready && typeof f.ready.then === "function") f.ready.then(otra);
    if (typeof f.addEventListener === "function") f.addEventListener("loadingdone", otra);
    return () => {
      vivo = false;
      if (typeof f.removeEventListener === "function") f.removeEventListener("loadingdone", otra);
    };
  }, []);

  const hojas = useMemo(() => {
    const lista = bloques.map((b) => ({ key: b.key, alto: medidas[b.key] || 0, juntoAlSiguiente: !!b.juntoAlSiguiente, cont: b.cont }));
    const altoCont = {};
    for (const g of Object.keys(conts)) altoCont[g] = medidas[`cont:${g}`] || 0;
    return paginar(lista, ALTO_UTIL_PX - MARGEN_SEGURIDAD_PX, altoCont);
  }, [bloques, conts, medidas]);

  const porKey = useMemo(() => new Map(bloques.map((b) => [b.key, b])), [bloques]);

  // Portada + hojas de contenido + cierre.
  const total = 1 + hojas.length + 1;

  return (
    <>
      <div className="habitia-acta acta-medidor" aria-hidden="true" ref={medidorRef}>
        <div className="medidor-cuerpo">
          {bloques.map((b) => (
            <div key={b.key} data-medida={b.key}>
              <Bloque b={b} primero={false} ultima={false} />
            </div>
          ))}
          {Object.entries(conts).map(([g, render]) => (
            <div key={`cont:${g}`} data-medida={`cont:${g}`}>
              <div className="bloque">{render()}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="habitia-acta" ref={ref} role="document" aria-label="Acta de Asamblea" style={zoom && zoom !== 1 ? { zoom } : undefined}>
        <CoverPage acta={acta} />
        {hojas.map((hoja, idx) => (
          <section className="page" key={`hoja-${idx}`}>
            <div className="page-flujo">
              {hoja.map((it, k) => {
                if (it.cont) {
                  return (
                    <div className="bloque" key={`cont-${it.key}`}>
                      {conts[it.key] ? conts[it.key]() : null}
                    </div>
                  );
                }
                const b = porKey.get(it.key);
                if (!b) return null;
                // Última fila del tramo de una tabla en esta hoja: cierra el borde.
                const sig = hoja[k + 1];
                const sigB = sig && !sig.cont ? porKey.get(sig.key) : null;
                const ultima = !!b.tabla && !(sigB && sigB.tabla === b.tabla);
                return <Bloque key={b.key} b={b} primero={k === 0} ultima={ultima} />;
              })}
            </div>
            <PageFooter nombre={acta.edificioNombre} n={idx + 2} total={total} />
          </section>
        ))}
        <ClosingPage acta={acta} coproById={coproById} n={total} total={total} />
      </div>
    </>
  );
});

export default ActaTemplate;
